import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import WeekdaysHeader from 'Components/UI/Calendar/WeekdaysHeader';
import DateNavigator from 'Components/UI/Calendar/DateNavigator';
import moment from 'moment-timezone';
import ScheduleDivider
  from 'Components/AdvisorApp/Viewport/TodayDashboard/ScheduleSection/ScheduleDailyView/DailyCalendar/ScheduleDivider';
import fr from 'facet-rest';
import {
  fetchDataFromLinkQueryParam,
  setNewLinkQueryParam, useAgentSchedulerDispatchContext,
  useAgentSchedulerStateContext,
} from 'Context/NewAgentSchedulerContext';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import CalendarSidebar from './CalendarSidebar';
import DayDisplayRow from './DayDisplayRow';
import DayColumn from './DayColumn';

const START_HOUR = 0;
const END_HOUR = 23;

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    position: 'relative',
  },
  calendarContainer: {
    display: 'flex',
    flex: 1,
    backgroundColor: theme.palette.common.white,
    borderRadius: '13px',
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'center',
  },
  eventContainer: {
    display: 'flex',
    flex: 1,
    position: 'relative',
    width: 1300,
    border: `1px solid ${theme.palette.facet.lightGray3}`,
    flexDirection: 'column',
    marginBottom: theme.spacing(10),
  },
  scheduleContainer: {
    display: 'flex',
    height: '100%',
    position: 'relative',
  },
  dayColumnContainer: {
    display: 'flex',
    flexDirection: 'row',
    '& > div:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.facet.lightGray3}`,
    },
  },
  loadingState: {
    height: '100%',
  },
  position: {},
  timeHeader: {
    display: 'flex',
    flexDirection: 'column',
    ...theme.typography.body1,
  },
  timeHeaderContainer: {
    position: 'absolute',
    left: '-100px',
    top: '-25px',
  },
  timeHeaderItems: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '5rem',
  },
});

const getDaysInWeekGivenSelectedDate = (selectedDate) => {
  const daysInWeek = [];
  const weekStartDate = moment(selectedDate).startOf('week');
  const weekEndDate = moment(selectedDate).endOf('week');

  daysInWeek.push(weekStartDate.clone());
  while (weekStartDate.add(1, 'day').diff(weekEndDate) < 0) {
    daysInWeek.push(weekStartDate.clone());
  }

  return daysInWeek;
};

const getHoursToDisplay = (windowStart, windowEnd) => {
  const hoursToDisplay = [];
  for (let i = moment(windowStart); i <= windowEnd; i = i.clone().add(1, 'h')) {
    hoursToDisplay.push(i);
  }
  return hoursToDisplay;
};

const WeeklyCalendar = ({
  className,
  classes,
}) => {
  const { householdResource } = useSimpleHousehold();
  const { loading, currentCollection } = useAgentSchedulerStateContext();
  const dispatch = useAgentSchedulerDispatchContext();
  const { selectedDate } = useAgentSchedulerStateContext();

  const availabilityResource = currentCollection.resources().head();
  const currentDate = moment(availabilityResource.attr('currentDate'));
  const prevDate = moment(availabilityResource.attr('prevDate'));
  const nextDate = moment(availabilityResource.attr('nextDate'));

  const availabilityGapsForWeek = availabilityResource.attr('gaps').resources().list();

  const browserTz = moment.tz.guess();
  const browserTzName = moment.tz(browserTz).zoneAbbr();
  const householdTzName = moment.tz(householdResource.attr('timeZone')).zoneAbbr();

  // set the time bounds for the events we want to display
  const windowStart = moment(currentDate)
    .startOf('day')
    .add(START_HOUR, 'h');

  const windowEnd = moment(currentDate)
    .startOf('day')
    .add(END_HOUR, 'h');

  const daysInWeek = getDaysInWeekGivenSelectedDate(currentDate);
  const hoursToDisplay = getHoursToDisplay(windowStart, windowEnd);

  const goToPrevWeek = () => {
    setNewLinkQueryParam(dispatch, availabilityResource.link('prev').url());
    fetchDataFromLinkQueryParam(dispatch);
  };

  const goToNextWeek = () => {
    setNewLinkQueryParam(dispatch, availabilityResource.link('next').url());
    fetchDataFromLinkQueryParam(dispatch);
  };

  const goToMonthlyView = () => {
    setNewLinkQueryParam(dispatch, availabilityResource.relationship(fr.relationships.availabilityPeriod.name).link('related').url());
    fetchDataFromLinkQueryParam(dispatch);
  };

  const goToNewMeeting = (gapResource) => {
    dispatch({
      type: 'setNewMeeting',
      selectedGapStart: gapResource.attr('start'),
      selectedGapEnd: gapResource.attr('end'),
      selectedMeetingDuration: availabilityResource.attr('meetingDuration'),
      selectedBufferDuration: availabilityResource.attr('meetingBuffer'),
      isAvailable: gapResource.attr('isAvailable'),
    });
    dispatch({ type: 'setPrevLink', prevLink: availabilityResource.link('self').url() });
    setNewLinkQueryParam(dispatch, gapResource.relationship(fr.relationships.scheduleCalendarEvent.name).link('related').url());
    fetchDataFromLinkQueryParam(dispatch);
  };

  return (
    <div
      className={classNames(classes.root, className)}
      test="scheduler-weekly-view"
    >
      <DateNavigator
        date={currentDate}
        prevDate={prevDate}
        nextDate={nextDate}
        goToPrevDate={goToPrevWeek}
        goToNextDate={goToNextWeek}
        goToNextDateEventName="Clicked Next Week From From MC Scheduler"
        goToPrevDateEventName="Clicked Previous Week From MC Scheduler"
        goToMonthlyView={goToMonthlyView}
        isLoading={loading}
        isWeekly={() => true} // TODO change this logic, should not need to do this
      />
      <WeekdaysHeader />
      <div className={classes.calendarContainer}>
        <div className={classes.eventContainer}>
          <CalendarSidebar availabilityResource={availabilityResource} />
          <DayDisplayRow
            daysInWeek={daysInWeek}
            selectedDate={selectedDate}
          />
          <div className={classes.scheduleContainer}>
            {householdResource.attr('timeZone') && (
              <div className={classes.timeHeaderContainer}>
                <div className={classes.timeHeaderItems}>
                  <div className={classes.timeHeader}>
                    <span>Client</span>
                    <span>({householdTzName})</span>
                  </div>
                  <div className={classes.timeHeader}>
                    <span>You</span>
                    <span>({browserTzName})</span>
                  </div>
                </div>
              </div>
            )}
            {hoursToDisplay.map(time => (
              <ScheduleDivider
                time={time}
                windowStart={windowStart}
                windowEnd={windowEnd}
                additionalTz={householdResource.attr('timeZone')}
              />
            ))}
            <div className={classes.dayColumnContainer}>
              {daysInWeek.map((day) => {
                const availabilityGaps = availabilityGapsForWeek.filter(gap => day.clone().isSame(gap.attr('start'), 'day'));
                return (
                  <DayColumn
                    day={day}
                    hourStart={START_HOUR}
                    hourEnd={END_HOUR}
                    availabilityGaps={availabilityGaps}
                    onAvailableClick={goToNewMeeting}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

WeeklyCalendar.defaultProps = {
  className: '',
};

WeeklyCalendar.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(WeeklyCalendar);
